<script setup lang="ts">
import type { Client } from '@metapic-apps/shared'
import { computed } from 'vue'

import EditIcon from '@/assets/icons/edit.svg?component'
import PermissionsIcon from '@/assets/icons/settings.svg?component'
import { formatRevenueShare } from '@/lib/adapters/clients.adapter'
import { config } from '@/lib/helpers'
import { BoxComponent, ButtonComponent, FlexComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{ client: Client }>()

const revenueShare = computed(() => formatRevenueShare(props.client.revenueShare))
</script>

<template>
  <BoxComponent :pt="22" :pl="24" :pr="24" :pb="24" class="list-item">
    <a :href="`${config.legacyAdminAppUrl}/#/clients/${client.id}/edit`">
      <FlexComponent justify="space-between" align="center" :gap="16">
        <BoxComponent w="50%">
          <BoxComponent :mb="4">
            <TypographyComponent variant="title-large" white-space="nowrap" text-truncate>{{ client.name }}</TypographyComponent>
          </BoxComponent>
          <TypographyComponent variant="body-small" color="var(--secondary-48)"> ID: {{ client.id }}</TypographyComponent>
        </BoxComponent>
        <BoxComponent w="25%">
          <TypographyComponent variant="label-medium" text-align="center">Revenue share</TypographyComponent>
          <TypographyComponent variant="title-large" text-align="center">{{ revenueShare }}</TypographyComponent>
        </BoxComponent>
        <BoxComponent w="25%">
          <TypographyComponent variant="label-medium" text-align="center">Payment system</TypographyComponent>
          <TypographyComponent variant="title-large" text-align="center">
            {{ client.hasOwnPaymentSystem ? 'Own system' : 'Metapic' }}
          </TypographyComponent>
        </BoxComponent>
      </FlexComponent>
    </a>
    <FlexComponent class="list-item__actions" :gap="8" align="center">
      <ButtonComponent :href="`${config.legacyAdminAppUrl}/#/clients/${client.id}/users`">
        <template #iconLeft>
          <el-icon color="var(--inverted)" class="icon">
            <PermissionsIcon />
          </el-icon>
        </template>
        Permissions
      </ButtonComponent>
      <ButtonComponent :href="`${config.legacyAdminAppUrl}/#/clients/${client.id}/edit`">
        <template #iconLeft>
          <el-icon color="var(--inverted)" class="icon">
            <EditIcon />
          </el-icon>
        </template>
        Edit
      </ButtonComponent>
    </FlexComponent>
  </BoxComponent>
</template>

<style scoped lang="scss">
.list-item {
  position: relative;

  border-radius: 16px;
  border: 1px solid var(--button-hover);
  background: var(--inverted);

  cursor: pointer;
  transition: background-color var(--transition) ease;

  &:hover {
    background-color: var(--secondary-2);

    .list-item__actions {
      opacity: 1;
    }
  }
}

.list-item__actions {
  position: absolute;
  top: 1px;
  right: 20px;
  bottom: 1px;

  opacity: 0;
  background-color: var(--secondary-2);

  transition: opacity var(--transition) ease;
}

:deep(.icon) {
  path {
    fill: currentColor;
  }
}
</style>
